import React from 'react';
import Alert from '@amzn/meridian/alert';
import Column from '@amzn/meridian/column';
import Theme from '@amzn/meridian/theme';
import { useAppSelector } from '../../store';
import { useSiteGatedFeature } from '../../hooks/useSiteGatedFeature';
import { IMAGE_QUALITY_FEATURE_NAME } from './constants';
import brightBluePalette from './brightBluePalette.json';

/** Same light/dark resolution as components/ThemeProvider, so the pages follow RCC's mode. */
export const useRccThemeMode = (): 'light' | 'dark' => {
  const { currentMode, darkModeFromParent } = useAppSelector(state => state.theme);
  if (currentMode === 'auto') return darkModeFromParent === 'DARK' ? 'dark' : 'light';
  return currentMode === 'dark' ? 'dark' : 'light';
};

/**
 * Feature gate for the image-quality pages (Evidence Image Quality and Associate Scorecards),
 * allowlisted in the facade via FeatureGating.RCC_IMAGE_QUALITY.AllowedUsers.
 *
 * The sidebar is not permission-aware, so its links show for everyone; this gate is what restricts
 * who sees the pages. Same resolution as FlowMapGated: nothing while the flag resolves (so a page
 * never fetches for someone who turns out not to be allowlisted), the page when enabled, a
 * coming-soon message otherwise; fails closed. Enabled pages render in their bright blue palette,
 * in RCC's light or dark mode.
 */
export const ImageQualityGate: React.FC<{
  pageName: string;
  testIdPrefix: string;
  children: React.ReactNode;
}> = ({ pageName, testIdPrefix, children }) => {
  const isEnabled = useSiteGatedFeature(IMAGE_QUALITY_FEATURE_NAME);
  const mode = useRccThemeMode();

  if (isEnabled === null) {
    return null;
  }

  if (!isEnabled) {
    return (
      <Column spacing="400" spacingInset="500" data-testid={`${testIdPrefix}-gated`}>
        <Alert type="neutral" data-testid={`${testIdPrefix}-not-enabled`}>
          {`${pageName} is coming soon and not yet available.`}
        </Alert>
      </Column>
    );
  }

  // Bright blue for these pages only (same shape as src/themes/customTheme.json); the rest of RCC keeps its purple palette.
  return (
    <Theme palette={brightBluePalette} mode={mode}>
      {children}
    </Theme>
  );
};
